/**
 * SimpleOrgGrid - Minimal org grid for debugging data flow
 *
 * Purpose: Strip down to bare minimum to debug:
 * 1. What fields come directly from the org object
 * 2. What fields need resolution (IDs -> objects)
 * 3. How optimistic updates flow through the system
 *
 * Route: /simple-grid
 */

import { Chip, Stack, Typography } from '@mui/material'
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium'
import { useContacts } from 'api/aws/useContacts'
import { useInlineMassUpdate } from 'api/aws/useInlineMassUpdate'
import { useUsers } from 'api/aws/useUsers'
import { useSearchData } from 'api/search'
import { pinnedColumns } from 'components/Organizations/grid/defaultModels'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useMemo, useState } from 'react'
import { gridContainerStyles } from 'styles/utilities'
import GlobalDataGrid from '../GlobalDataGrid'
import { selectEditCell, userEditCell } from './editCells'
import { ORG_STATUS_OPTIONS, ORG_TYPE_OPTIONS, VERTICAL_OPTIONS } from './config/options'

// Editable fields - these are the fields we can update via inline editing
const EDITABLE_FIELDS = [
  'status',
  'type',
  'vertical',
  'owner',
  'primaryContact',
  'contacts',
  'contractedBy',
  'affiliated',
  'team.AM',
  'team.PM',
  'team.BDE',
  'team.DMS',
  'team.MIA',
  'team.QA',
  'team.SEO',
  'team.other',
]

export const SimpleOrgGrid: React.FC = () => {
  const { pinned } = useLayoutContext()
  const apiRef = useGridApiRef()
  const { organizations, isLoading } = useSearchData()
  const { users } = useUsers(true)
  const { contacts } = useContacts(true)

  // Build lookup maps for debugging
  const userMap = useMemo(() => {
    const map = new Map<string, any>()
    users?.forEach((u: any) => {
      map.set(u.user_uuid, u)
      map.set(u.id, u) // Slack IDs too
    })
    return map
  }, [users])

  const contactMap = useMemo(() => {
    const map = new Map<string, any>()
    contacts?.forEach((c: any) => map.set(c.contacts_uuid, c))
    return map
  }, [contacts])

  // Transform rows - add rowId for GlobalDataGrid
  const rows = useMemo(() => {
    if (!organizations?.length) return []
    return organizations.map(org => ({
      ...org,
      rowId: org.PK || org.org_uuid,
    }))
  }, [organizations])

  // Helper to render array of IDs as chips
  const renderIdChips = (ids: any[], color: 'warning' | 'success' | 'error' = 'warning') => {
    if (!ids || !Array.isArray(ids) || !ids.length) return <Typography variant="caption">-</Typography>
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {ids.map((id: string, idx: number) => (
          // <Chip
          //   key={id || idx}
          //   label={typeof id === 'string' ? id.slice(0, 8) + '...' : JSON.stringify(id).slice(0, 10)}
          //   size="small"
          //   color={color}
          //   title={typeof id === 'string' ? `Full ID: ${id}` : JSON.stringify(id)}
          // />
          <Typography key={idx}>{id}</Typography>
        ))}
      </Stack>
    )
  }

  // Helper to render resolved users
  const renderResolvedUsers = (users: any[]) => {
    if (!users || !Array.isArray(users) || !users.length) return <Typography variant="caption">-</Typography>
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {users.map((u: any) => (
          <Chip key={u?.user_uuid || u?.id} label={u?.name || 'Unknown'} size="small" color="success" />
        ))}
      </Stack>
    )
  }

  // Helper to render resolved contacts
  const renderResolvedContacts = (contacts: any[]) => {
    if (!contacts || !Array.isArray(contacts) || !contacts.length) return <Typography variant="caption">-</Typography>
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {contacts.map((c: any) => (
          <Chip key={c?.contacts_uuid} label={c?.name || c?.email || 'Unknown'} size="small" color="success" />
        ))}
      </Stack>
    )
  }

  // Helper to render resolved orgs
  const renderResolvedOrgs = (orgs: any[]) => {
    if (!orgs || !Array.isArray(orgs) || !orgs.length) return <Typography variant="caption">-</Typography>
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {orgs.map((o: any) => (
          <Chip key={o?.org_uuid || o?.PK} label={o?.name || 'Unknown'} size="small" color="success" />
        ))}
      </Stack>
    )
  }

  // All columns - show raw values and what needs resolution
  const columns: GridColDef[] = useMemo(
    () => [
      // === IDENTITY FIELDS ===
      {
        field: 'org_uuid',
        headerName: 'org_uuid',
        width: 120,
        description: 'Direct - unique identifier',
        renderCell: ({ row }) => (
          <Typography variant="caption" title={row.org_uuid}>
            {row.org_uuid?.slice(0, 8)}...
          </Typography>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 180,
        description: 'Direct string',
      },

      // === DIRECT STRING FIELDS ===
      {
        field: 'status',
        headerName: 'status',
        width: 100,
        editable: true,
        description: 'Direct string',
        renderEditCell: selectEditCell(ORG_STATUS_OPTIONS)
      },
      {
        field: 'type',
        headerName: 'type',
        width: 150,
        editable: true,
        description: 'Direct string',
        renderEditCell: selectEditCell(ORG_TYPE_OPTIONS),
      },
      {
        field: 'vertical',
        headerName: 'vertical',
        width: 150,
        editable: true,
        description: 'Direct string',
        renderEditCell: selectEditCell(VERTICAL_OPTIONS),
      },
      {
        field: 'discovery',
        headerName: 'discovery',
        width: 120,
        description: 'Direct string',
      },
      {
        field: 'website',
        headerName: 'website',
        width: 150,
        description: 'Direct string',
      },
      {
        field: 'trackingType',
        headerName: 'trackingType',
        width: 120,
        description: 'Direct string',
      },
      {
        field: 'social',
        headerName: 'social',
        width: 100,
        description: 'Direct object',
        renderCell: ({ row }) => (
          <Typography variant="caption">{row.social ? JSON.stringify(row.social).slice(0, 20) : '-'}</Typography>
        ),
      },

      // === NUMERIC FIELDS ===
      {
        field: 'total',
        headerName: 'total',
        width: 100,
        type: 'number',
        description: 'Direct number',
      },
      {
        field: 'monthlyTotal',
        headerName: 'monthlyTotal',
        width: 110,
        type: 'number',
        description: 'Direct number',
      },
      {
        field: 'spend',
        headerName: 'spend',
        width: 100,
        type: 'number',
        description: 'Direct number',
      },
      {
        field: 'grossProfit',
        headerName: 'grossProfit',
        width: 100,
        type: 'number',
        description: 'Direct number',
      },
      {
        field: 'totalGrossProfit',
        headerName: 'totalGrossProfit',
        width: 120,
        type: 'number',
        description: 'Direct number',
      },
      {
        field: 'implementationFee',
        headerName: 'implementationFee',
        width: 130,
        type: 'number',
        description: 'Direct number',
      },

      // === DATE FIELDS ===
      {
        field: 'creation_timestamp',
        headerName: 'creation_timestamp',
        width: 160,
        description: 'Direct date string',
        renderCell: ({ row }) => (
          <Typography variant="caption">
            {row.creation_timestamp ? new Date(row.creation_timestamp).toLocaleDateString() : '-'}
          </Typography>
        ),
      },

      // === OWNER (User Reference) ===
      {
        field: 'owner_raw',
        headerName: 'owner (raw)',
        width: 150,
        description: 'Array of user UUIDs',
        renderCell: ({ row }) => renderIdChips(row.owner),
      },
      {
        field: '_resolvedOwner',
        headerName: 'owner (resolved)',
        width: 150,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => {
          const owner = row._resolvedOwner
          if (!owner) return <Chip label="Not resolved" size="small" color="error" />
          if (Array.isArray(owner)) return renderResolvedUsers(owner)
          return <Chip label={owner.name} size="small" color="success" />
        },
      },

      // === PRIMARY CONTACT (Contact Reference) ===
      {
        field: 'primaryContact_raw',
        headerName: 'primaryContact (raw)',
        width: 200,
        description: 'Array of contact UUIDs',
        renderCell: ({ row }) => renderIdChips(row.primaryContact),
      },
      {
        field: '_resolvedPrimaryContact',
        headerName: 'primaryContact (resolved)',
        width: 180,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => {
          const contact = row._resolvedPrimaryContact
          if (!contact) return <Chip label="Not resolved" size="small" color="error" />
          if (Array.isArray(contact)) return renderResolvedContacts(contact)
          return <Chip label={contact.name || contact.email} size="small" color="success" />
        },
      },
      // DEBUG: Check if contact exists in client-side contactMap
      {
        field: 'primaryContact_debug',
        headerName: 'primaryContact (client lookup)',
        width: 220,
        description: 'Client-side lookup using contactMap from useContacts',
        renderCell: ({ row }) => {
          const ids = row.primaryContact || []
          if (!ids.length) return <Typography variant="caption">-</Typography>
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {ids.map((id: string) => {
                const found = contactMap.get(id)
                if (found) {
                  return (
                    <Chip
                      key={id}
                      label={`✓ ${found.name || found.email}`}
                      size="small"
                      color="info"
                      title={`Found in contactMap: ${JSON.stringify(found)}`}
                    />
                  )
                }
                return (
                  <Chip
                    key={id}
                    label={`✗ ${id.slice(0, 8)}...`}
                    size="small"
                    color="error"
                    title={`NOT in contactMap. Full ID: ${id}`}
                  />
                )
              })}
            </Stack>
          )
        },
      },

      // === CONTACTS (Contact References) ===
      {
        field: 'contacts_raw',
        headerName: 'contacts (raw)',
        width: 160,
        description: 'Array of contact UUIDs',
        renderCell: ({ row }) => renderIdChips(row.contacts),
      },
      {
        field: '_resolvedContacts',
        headerName: 'contacts (resolved)',
        width: 180,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedContacts(row._resolvedContacts),
      },

      // === ORG REFERENCES ===
      {
        field: 'contractedBy_raw',
        headerName: 'contractedBy (raw)',
        width: 160,
        description: 'Array of org UUIDs',
        renderCell: ({ row }) => renderIdChips(row.contractedBy),
      },
      {
        field: '_resolvedContractedBy',
        headerName: 'contractedBy (resolved)',
        width: 180,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedOrgs(row._resolvedContractedBy),
      },
      {
        field: 'affiliated_raw',
        headerName: 'affiliated (raw)',
        width: 160,
        description: 'Array of org UUIDs',
        renderCell: ({ row }) => renderIdChips(row.affiliated),
      },
      {
        field: '_resolvedAffiliated',
        headerName: 'affiliated (resolved)',
        width: 180,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedOrgs(row._resolvedAffiliated),
      },
      {
        field: 'orgNameReference',
        headerName: 'orgNameReference',
        width: 150,
        description: 'Org name reference string',
      },

      // === TEAM ROLES (Nested User References) ===
      {
        field: 'team_details',
        headerName: 'team (raw object)',
        width: 200,
        description: 'Raw team object with role arrays',
        renderCell: ({ row }) => (
          <Typography variant="caption" title={JSON.stringify(row.team)}>
            {row.team ? `{AM:${row.team.AM?.length || 0}, PM:${row.team.PM?.length || 0}, ...}` : '-'}
          </Typography>
        ),
      },

      // Team.AM - EDITABLE
      {
        field: 'team.AM',
        headerName: '✏️ team.AM (edit)',
        width: 200,
        editable: true,
        description: 'EDITABLE - Click to edit team AM members',
        // Display using CLIENT-SIDE resolution for optimistic updates
        // Instead of relying on _resolvedTeam.AM (which is stale until server refreshes),
        // we resolve team.AM IDs using the userMap on every render
        renderCell: ({ row }) => {
          const rawIds = row.team?.AM || []
          if (!rawIds.length) {
            return (
              <Typography variant="caption" color="text.secondary">
                -
              </Typography>
            )
          }
          // Client-side resolution: look up each ID in userMap
          const resolvedUsers = rawIds.map((id: string) => userMap.get(id)).filter(Boolean)

          if (resolvedUsers.length > 0) {
            return renderResolvedUsers(resolvedUsers)
          }
          // Fallback to showing IDs if not found in userMap
          return renderIdChips(rawIds, 'warning')
        },
        // Get value for editing (the raw IDs)
        valueGetter: (value, row) => row.team?.AM || [],
        // Set value after editing
        valueSetter: (value, row) => {
          return {
            ...row,
            team: {
              ...row.team,
              AM: value,
            },
          }
        },
        // Use our custom user edit cell
        renderEditCell: userEditCell({ users }, true), // true = multiple
      },
      // Debug columns to compare client-side vs server-side resolution
      {
        field: 'AM_raw',
        headerName: 'team.AM (raw IDs)',
        width: 150,
        description: 'Raw UUIDs stored in team.AM',
        renderCell: ({ row }) => renderIdChips(row.team?.AM),
      },
      {
        field: 'AM_client_resolved',
        headerName: '🟢 Client Resolved',
        width: 180,
        description: 'Resolved using userMap (updates optimistically)',
        renderCell: ({ row }) => {
          const rawIds = row.team?.AM || []
          if (!rawIds.length) return <Typography variant="caption">-</Typography>
          const resolved = rawIds.map((id: string) => userMap.get(id)).filter(Boolean)
          return resolved.length > 0 ? renderResolvedUsers(resolved) : renderIdChips(rawIds, 'error')
        },
      },
      {
        field: 'AM_server_resolved',
        headerName: '🔴 Server Resolved',
        width: 180,
        description: '_resolvedTeam.AM from server (stale until refresh)',
        renderCell: ({ row }) => renderResolvedUsers(row._resolvedTeam?.AM),
      },

      // Team.BDE
      {
        field: 'BDE_raw',
        headerName: 'team.BDE (raw)',
        width: 150,
        description: 'team.BDE array of user UUIDs',
        renderCell: ({ row }) => renderIdChips(row.team?.BDE),
      },
      {
        field: 'BDE_resolved',
        headerName: 'team.BDE (resolved)',
        width: 150,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedUsers(row._resolvedTeam?.BDE),
      },

      // Team.DMS
      {
        field: 'DMS_raw',
        headerName: 'team.DMS (raw)',
        width: 150,
        description: 'team.DMS array of user UUIDs',
        renderCell: ({ row }) => renderIdChips(row.team?.DMS),
      },
      {
        field: 'DMS_resolved',
        headerName: 'team.DMS (resolved)',
        width: 150,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedUsers(row._resolvedTeam?.DMS),
      },

      // Team.MIA
      {
        field: 'MIA_raw',
        headerName: 'team.MIA (raw)',
        width: 150,
        description: 'team.MIA array of user UUIDs',
        renderCell: ({ row }) => renderIdChips(row.team?.MIA),
      },
      {
        field: 'MIA_resolved',
        headerName: 'team.MIA (resolved)',
        width: 150,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedUsers(row._resolvedTeam?.MIA),
      },

      // Team.PM
      {
        field: 'PM_raw',
        headerName: 'team.PM (raw)',
        width: 150,
        description: 'team.PM array of user UUIDs',
        renderCell: ({ row }) => renderIdChips(row.team?.PM),
      },
      {
        field: 'PM_resolved',
        headerName: 'team.PM (resolved)',
        width: 150,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedUsers(row._resolvedTeam?.PM),
      },

      // Team.QA
      {
        field: 'QA_raw',
        headerName: 'team.QA (raw)',
        width: 150,
        description: 'team.QA array of user UUIDs',
        renderCell: ({ row }) => renderIdChips(row.team?.QA),
      },
      {
        field: 'QA_resolved',
        headerName: 'team.QA (resolved)',
        width: 150,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedUsers(row._resolvedTeam?.QA),
      },

      // Team.SEO
      {
        field: 'SEO_raw',
        headerName: 'team.SEO (raw)',
        width: 150,
        description: 'team.SEO array of user UUIDs',
        renderCell: ({ row }) => renderIdChips(row.team?.SEO),
      },
      {
        field: 'SEO_resolved',
        headerName: 'team.SEO (resolved)',
        width: 150,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedUsers(row._resolvedTeam?.SEO),
      },

      // Team.other
      {
        field: 'other_raw',
        headerName: 'team.other (raw)',
        width: 150,
        description: 'team.other array of user UUIDs',
        renderCell: ({ row }) => renderIdChips(row.team?.other),
      },
      {
        field: 'other_resolved',
        headerName: 'team.other (resolved)',
        width: 150,
        description: 'Pre-resolved by API',
        renderCell: ({ row }) => renderResolvedUsers(row._resolvedTeam?.other),
      },
    ],
    [users, userMap, contactMap] // Re-create columns when lookup data loads
  )

  // Simple row selection
  const [selectionModel, setSelectionModel] = useState<any>({ ids: new Set(), type: 'include' })

  // Use the real inline mass update hook - handles both single and batch updates
  const { processRowUpdate, isUpdating } = useInlineMassUpdate({
    entityType: 'organization',
    context: 'organizations',
    rowSelectionModel: selectionModel,
    apiRef,
    editableFields: EDITABLE_FIELDS,
    onMassUpdateComplete: (count, field, value) => {
      console.log(`[SimpleOrgGrid] Mass update complete: ${count} rows, field: ${field}, value:`, value)
    },
  })
  const gridContainerSx = {
    position: 'relative',
    ...gridContainerStyles,
    pt: pinned ? '60px' : 0.8,
    pb: 2,
    transition: 'all 0.3s ease-in-out',
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
  }
  return (
    <GlobalDataGrid
      rows={rows}
      columns={columns}
      apiRef={apiRef}
      context="test-simple"
      defaultFilterModel={{ items: [] }}
      defaultVisibilityModel={{}}
      defaultSortModel={[]}
      defaultGridDensity="compact"
      treeData={false}
      checkboxSelection={true}
      pinnedColumns={pinnedColumns}
      onRowSelectionModelChange={setSelectionModel}
      onProcessRowUpdate={processRowUpdate}
      allowInlineMassEdit={true}
      isLoading={isLoading || isUpdating}
      // gridSxProps={{
      //   height: '',
      //   '& .MuiDataGrid-cell': {
      //     py: 0.5,
      //   },
      // }}
    />
  )
}

export default SimpleOrgGrid
